import {
  Currency,
  isString,
  customTreesCosts as treeUnitPricePerTree,
  customOffsetsCosts as carbonOffsetPricePerTonne,
  round,
} from '..';
import { Decimal } from 'decimal.js';
import { AreaUnit, CarbonUnit, CM2_PER_M2, GRAMS_PER_KILOGRAM, GRAMS_PER_TONNE, ImpactType, ImpactUnit } from './units';

/**
 * A base unit is the unit of measurement for a quantity of impact
 *
 *  - TREE:                just a tree
 *  - CARBON_OFFSETS:      grams
 *  - CARBON_REMOVAL:      grams
 *  - HABITAT_RESTORATION: centimeters squared
 *
 * The base unit amount is the unit value per unit quantity
 */

export const gramsToKg = (grams: number) => round(grams / 1000, 4);
export const gramsToTonnes = (grams: number) => round(grams / 1e6, 4);
export const kgToTonnes = (kg: number) => round(kg / 1000, 4);
export const kgToGrams = (kg: number) => round(kg * 1000, 4);
export const tonnesToGrams = (tonnes: number) => round(tonnes * 1e6, 4);
export const tonnesToKg = (tonnes: number) => round(tonnes * 1000, 4);
export const cmSquaredToMSquared = (cm2: number) => round(cm2 / 1e4, 4);
export const mSquaredToCmSquared = (m2: number) => round(m2 * 1e4, 4);

type GetAmountPerBaseUnitArgs<T extends ImpactType = ImpactType> = {
  [type in T]: {
    type: type;
    currency: Currency;
  } & {
    [ImpactType.TREES]: { amountPerUnit: Decimal | 'use-latest' };
    [ImpactType.CARBON_OFFSETS]:
      | { unit: CarbonUnit; amountPerUnit: Decimal } // Converting from $/tonne into $/g
      | { amountPerUnit: 'use-latest' }; // Getting the current amountPerUnit
    [ImpactType.CARBON_REMOVAL]: { unit: CarbonUnit; amountPerUnit: Decimal };
    [ImpactType.HABITAT_RESTORATION]: { unit: AreaUnit; amountPerUnit: Decimal };
  }[type];
}[T];

/**
 * Amounts can be supplied in one of several unit types. This returns an adjusted 'amountPerUnit' which is correct in relation
 * to the base unit type used in the database.
 *
 * This factors in the need to convert from amounts expressed in euros, dollars, etc to amounts expressed in cents, by multiplying by 100.
 *
 * TODO: The `.times(100)` multiplier can be removed once our hardcoded costs are represented as integers rather than floats (e.g.
 * once they're represented as pence rather than pounds, cents rather than dollars, etc)
 */
export const getAmountPerBaseUnit = (args: GetAmountPerBaseUnitArgs): Decimal => {
  // Use the amount/unit from @ecologi/shared
  if (args.type == ImpactType.TREES) {
    if (args.amountPerUnit === 'use-latest') {
      const unitPriceForCurrency = treeUnitPricePerTree[args.currency];

      return new Decimal(unitPriceForCurrency).times(100);
    }

    // Tree value is always amount/tree - not a divisble quantity
    return args.amountPerUnit;
  }

  if (args.type == ImpactType.CARBON_OFFSETS) {
    if (isString(args.amountPerUnit) && args.amountPerUnit == 'use-latest') {
      const pricePerTonneForCurrency = carbonOffsetPricePerTonne[args.currency];

      return fromUnitAmountIntoBaseUnitAmount(new Decimal(pricePerTonneForCurrency).times(100), 'tonnes');
    }

    if ('unit' in args) return fromUnitAmountIntoBaseUnitAmount(args.amountPerUnit, args.unit);
  }

  if (args.type == ImpactType.CARBON_REMOVAL || args.type == ImpactType.HABITAT_RESTORATION) {
    return fromUnitAmountIntoBaseUnitAmount(args.amountPerUnit, args.unit);
  }

  throw new Error(`Invalid impact type supplied`);
};

/**
 * Units can be supplied as one of several types. This converts from the supplied unit type into the base unit type:
 *   - trees:           single tree
 *   - carbon removals: grams
 *   - carbon offsets:  grams
 */
export const fromUnitQuantityIntoBaseUnitQuantity = (quantity: number, unit: ImpactUnit) => {
  const conversionTable: Record<ImpactUnit, (quantity: number) => number> = {
    grams: grams => grams,
    kilograms: kg => kg * GRAMS_PER_KILOGRAM,
    tonnes: tonnes => tonnes * GRAMS_PER_TONNE,
    m2: m2 => m2 * CM2_PER_M2,
    cm2: cm2 => cm2,
  };

  // No floating point values
  return Number(conversionTable[unit](quantity).toFixed(0));
};

// Inverse of `fromUnitQuantityIntoBaseUnitQuantity`.
export const fromBaseUnitQuantityIntoUnitQuantity = (baseQuantity: number, unit: ImpactUnit) => {
  const conversionTable: Record<ImpactUnit, (quantity: number) => number> = {
    grams: grams => grams,
    kilograms: grams => grams / GRAMS_PER_KILOGRAM,
    tonnes: grams => grams / GRAMS_PER_TONNE,
    m2: cm2 => cm2 / CM2_PER_M2,
    cm2: cm2 => cm2,
  };

  return conversionTable[unit](baseQuantity);
};

/**
 * Converts from $1.25/tonne into $0.00000125/gram etc.
 */
const fromUnitAmountIntoBaseUnitAmount = (amountPerUnit: Decimal, unit: ImpactUnit): Decimal => {
  const conversionTable: Record<ImpactUnit, (amount: Decimal) => Decimal> = {
    m2: m2 => m2.dividedBy(CM2_PER_M2),
    cm2: cm2 => cm2,
    grams: grams => grams,
    kilograms: amountPerKg => amountPerKg.dividedBy(GRAMS_PER_KILOGRAM),
    tonnes: amountPerTonne => amountPerTonne.dividedBy(GRAMS_PER_TONNE),
  };

  return conversionTable[unit](amountPerUnit);
};
